import React, { useContext, useState } from 'react';
import { ApiContext } from 'src/context/apiContext';
import { useParams } from 'react-router-dom';
import { ModelVersionModal } from 'src/components/ModelVersionModal';
import { useModelVersions } from 'src/hooks/useModelVersions';
import { Link } from 'src/components/Link';
import { ClientSideTable } from 'src/components/ClientSideTable';
import { Application } from 'src/types/application';
import { SpaceBetween } from '@cloudscape-design/components';
import { PermissionAwareButton } from 'src/components/PermissionAwareButton';

export function ModelVersionList() {
  const { dispatch } = useContext(ApiContext);
  const { accountNo, applicationId } = useParams();
  const [modelVersionModalVisible, setModelVersionModalVisible] =
    useState<boolean>(false);
  const [modelVersionModalMode, setModelVersionModalMode] = useState<
    'New' | 'Edit' | 'Delete'
  >('New');
  const [modelVersionModalValues, setModelVersionModalValues] =
    useState<any>(null);

  const showModelVersionModal = (
    mode: 'New' | 'Edit' | 'Delete',
    values: any,
  ) => {
    setModelVersionModalValues(values);
    setModelVersionModalMode(mode);
    setModelVersionModalVisible(true);
  };

  const { data, isLoading, error } = useModelVersions({ applicationId });

  return (
    <>
      <ClientSideTable
        error={error}
        loading={isLoading}
        title="Model Versions"
        actions={
          <PermissionAwareButton
            wrapText={false}
            requiredPermission="ADMINISTER"
            actionDescription="create model versions"
            variant="primary"
            onClick={() => {
              showModelVersionModal('New', null);
            }}
          >
            New Model Version
          </PermissionAwareButton>
        }
        sortColumn="updateDate"
        isDescending={true}
        data={data || []}
        columnDefinitions={[
          {
            header: 'Model Version Name',
            sortingField: 'name',
            isRowHeader: true,
            id: 'model-version-name',
            cell: (item: ModelVersion) => (
              <Link
                to={`/accounts/${accountNo}/applications/${applicationId}/modelVersions/${item.id}/`}
                onClick={() => {
                  dispatch({
                    type: 'setCurrentModelVersionId',
                    payload: item.id,
                  });
                }}
              >
                {item.name}
              </Link>
            ),
          },
          {
            header: 'Model Version ID',
            cell: (item: ModelVersion) => item.id,
            sortingField: 'id',
            id: 'model-version-id',
            isRowHeader: true,
          },
          {
            header: 'Description',
            cell: (item: ModelVersion) => item.description,
            id: 'description',
            sortingField: 'description',
            isRowHeader: true,
          },
          {
            id: 'updatedDate',
            header: 'Updated Date',
            cell: (item: Application) => {
              const date = new Date(parseInt(item.updatedDate) * 1000);
              return date.toUTCString();
            },
            sortingField: 'updatedDate',
          },
          {
            header: 'Updated By',
            cell: (item: ModelVersion) => item.updatedBy,
            sortingField: 'updatedBy',
            isRowHeader: true,
            id: 'updated-by',
          },
          {
            header: '',
            id: 'actions',
            cell: (item: Application) => (
              <SpaceBetween
                direction="horizontal"
                size="xs"
                alignItems="center"
              >
                <PermissionAwareButton
                  onClick={() => showModelVersionModal('Edit', item)}
                  variant="normal"
                  wrapText={false}
                  requiredPermission="ADMINISTER"
                  actionDescription="edit model versions"
                >
                  Edit
                </PermissionAwareButton>
                <PermissionAwareButton
                  onClick={() => showModelVersionModal('Delete', item)}
                  variant="normal"
                  wrapText={false}
                  requiredPermission="ADMINISTER"
                  actionDescription="delete model versions"
                >
                  Delete
                </PermissionAwareButton>
              </SpaceBetween>
            ),
          },
        ]}
      />
      {applicationId && (
        <ModelVersionModal
          visible={modelVersionModalVisible}
          modalMode={modelVersionModalMode}
          values={modelVersionModalValues}
          onClose={() => {
            setModelVersionModalVisible(false);
          }}
          applicationId={applicationId}
        />
      )}
    </>
  );
}
